import React, { useState, useEffect, useCallback, useRef } from 'react';
import useDraggable from '../hooks/useDraggable';
import useTrackPageView from '../hooks/useTrackPageView';
import { useTranslation } from '../i18n/LanguageContext';

const CATEGORIES = ['technical', 'account', 'billing', 'bot', 'other'];
const STATUSES   = ['open', 'in_progress', 'resolved', 'closed'];
const STATUS_COLORS = { open: '#3b82f6', in_progress: '#f59e0b', resolved: '#10b981', closed: '#6b7280' };
const STATUS_KEYS = { open: 'statusOpen', in_progress: 'statusInProgress', resolved: 'statusResolved', closed: 'statusClosed' };
const CATEGORY_KEYS = { technical: 'catTechnical', account: 'catAccount', billing: 'catBilling', bot: 'catBot', other: 'catOther' };

function fmtDate(iso, lang) {
  if (!iso) return '';
  return new Date(iso).toLocaleString(lang === 'en' ? 'en-US' : 'tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function StatusPill({ status, t }) {
  const color = STATUS_COLORS[status] || '#6b7280';
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
      background: `${color}22`, color, border: `1px solid ${color}55`, whiteSpace: 'nowrap',
    }}>
      {t(`support.${STATUS_KEYS[status]}`)}
    </span>
  );
}

export default function SupportModal({ token, isSystemAdmin, onClose }) {
  const { t, lang } = useTranslation();
  const isSystem = !!isSystemAdmin;
  const { offset: dragOffset, onMouseDown: handleHeaderMouseDown } = useDraggable();

  const [view, setView] = useState('list'); // 'list' | 'create' | 'detail'
  const [tickets,   setTickets]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [listError, setListError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [ticket,     setTicket]     = useState(null);
  const [messages,   setMessages]   = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError,   setDetailError]   = useState(null);

  const [subject,  setSubject]  = useState('');
  const [category, setCategory] = useState('other');
  const [message,  setMessage]  = useState('');
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState(null);

  const [reply,   setReply]   = useState('');
  const [sending, setSending] = useState(false);

  const bottomRef = useRef(null);

  useTrackPageView(isSystem ? 'Destek - Tum Talepler' : 'Destek - Taleplerim', view === 'list');
  useTrackPageView('Destek - Talep Detayi', view === 'detail');

  const loadTickets = useCallback(() => {
    setLoading(true); setListError(null);
    const url = isSystem
      ? `/api/support/admin/tickets${statusFilter ? `?status=${statusFilter}` : ''}`
      : '/api/support/tickets';
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (!ok) throw new Error(d.error || t('support.loadFailed')); setTickets(d); })
      .catch(err => setListError(err.message))
      .finally(() => setLoading(false));
  }, [token, isSystem, statusFilter]); // eslint-disable-line

  useEffect(() => { if (view === 'list') loadTickets(); }, [view, loadTickets]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'nearest' }); }, [messages]);

  const openTicket = (id) => {
    setSelectedId(id); setView('detail'); setDetailLoading(true); setDetailError(null);
    fetch(`/api/support/tickets/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d.error || t('support.loadFailed'));
        setTicket(d.ticket); setMessages(d.messages);
      })
      .catch(err => setDetailError(err.message))
      .finally(() => setDetailLoading(false));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true); setCreateErr(null);
    try {
      const res = await fetch('/api/support/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subject, category, message }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('support.createFailed'));
      setSubject(''); setMessage(''); setCategory('other');
      openTicket(data.id);
    } catch (err) {
      setCreateErr(err.message);
    } finally {
      setCreating(false);
    }
  };

  const sendReply = async () => {
    const content = reply.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const res = await fetch(`/api/support/tickets/${selectedId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('support.sendFailed'));
      setMessages(m => [...m, { ...data, sender_name: isSystem ? t('support.supportTeamLabel') : t('support.you') }]);
      setReply('');
      setTicket(tk => {
        if (!tk || ['resolved', 'closed'].includes(tk.status)) return tk;
        return { ...tk, status: isSystem ? 'in_progress' : 'open' };
      });
    } catch (err) {
      setDetailError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleReplyKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); }
  };

  const handleCloseTicket = async () => {
    if (!window.confirm(t('support.confirmClose'))) return;
    try {
      await fetch(`/api/support/tickets/${selectedId}/close`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
      setTicket(tk => tk ? { ...tk, status: 'closed' } : tk);
    } catch { /* sessizce yoksay — kullanıcı listeye dönünce zaten güncel durum çekilir */ }
  };

  const handleStatusChange = async (status) => {
    try {
      await fetch(`/api/support/tickets/${selectedId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      setTicket(tk => tk ? { ...tk, status } : tk);
    } catch { /* yoksay */ }
  };

  const isMine = (msg) => (isSystem ? msg.is_admin : !msg.is_admin);
  const isClosed = ticket?.status === 'closed';

  return (
    <div className="sh-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="sh-modal" style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`, width: 640 }}>
        <div className="sh-header" onMouseDown={handleHeaderMouseDown}>
          <div>
            <h2 className="sh-title">🎧 {t('support.title')}</h2>
            <p className="sh-subtitle">{isSystem ? t('support.allTickets') : t('support.myTickets')}</p>
          </div>
          <button className="sh-close" onClick={onClose}>✕</button>
        </div>

        <div className="sh-body" style={{ minHeight: 320 }}>
          {view === 'list' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {isSystem ? (
                  <select
                    className="chart-interval-select"
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    style={{ padding: '6px 8px' }}
                  >
                    <option value="">{t('support.statusFilterAll')}</option>
                    {STATUSES.map(s => <option key={s} value={s}>{t(`support.${STATUS_KEYS[s]}`)}</option>)}
                  </select>
                ) : <div />}
                {!isSystem && (
                  <button type="button" className="btn btn-start" onClick={() => { setCreateErr(null); setView('create'); }}>
                    {t('support.createButton')}
                  </button>
                )}
              </div>

              {loading && <p className="settings-note">{t('common.loading')}</p>}
              {listError && <div className="msg msg-error">{listError}</div>}
              {!loading && tickets.length === 0 && (
                <p className="settings-note">{isSystem ? t('support.noTicketsAdmin') : t('support.noTickets')}</p>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {tickets.map(tk => (
                  <div
                    key={tk.id}
                    onClick={() => openTicket(tk.id)}
                    style={{
                      background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8,
                      padding: '10px 12px', cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <strong style={{ fontSize: 13 }}>{tk.subject}</strong>
                      <StatusPill status={tk.status} t={t} />
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4 }}>
                      {t(`support.${CATEGORY_KEYS[tk.category]}`)}
                      {isSystem && <> · {tk.user_name} ({tk.user_email})</>}
                      {' · '}{t('support.messageCount', { n: tk.message_count })}
                      {tk.last_message_at && <> · {t('support.lastMessage', { time: fmtDate(tk.last_message_at, lang) })}</>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {view === 'create' && (
            <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="settings-field">
                <label className="settings-label">{t('support.subjectLabel')}</label>
                <input
                  className="settings-input" type="text" value={subject}
                  placeholder={t('support.subjectPlaceholder')}
                  onChange={e => setSubject(e.target.value)} maxLength={200} required
                />
              </div>
              <div className="settings-field">
                <label className="settings-label">{t('support.categoryLabel')}</label>
                <select className="settings-input" value={category} onChange={e => setCategory(e.target.value)}>
                  {CATEGORIES.map(c => (
                    <option key={c} value={c}>{t(`support.${CATEGORY_KEYS[c]}`)}</option>
                  ))}
                </select>
              </div>
              <div className="settings-field">
                <label className="settings-label">{t('support.messageLabel')}</label>
                <textarea
                  className="settings-input" rows={5} value={message}
                  placeholder={t('support.messagePlaceholder')}
                  onChange={e => setMessage(e.target.value)} maxLength={2000} required
                  style={{ resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
              {createErr && <div className="msg msg-error">{createErr}</div>}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="btn" onClick={() => setView('list')} disabled={creating}>{t('support.cancelButton')}</button>
                <button type="submit" className="btn btn-start" disabled={creating}>
                  {creating ? t('common.saving') : t('support.submitButton')}
                </button>
              </div>
            </form>
          )}

          {view === 'detail' && (
            <>
              <button type="button" className="auth-legal-link" style={{ fontSize: 12, marginBottom: 4 }} onClick={() => setView('list')}>
                {t('support.backToList')}
              </button>
              {detailLoading && <p className="settings-note">{t('common.loading')}</p>}
              {detailError && <div className="msg msg-error">{detailError}</div>}
              {ticket && !detailLoading && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div>
                      <strong style={{ fontSize: 14 }}>{ticket.subject}</strong>
                      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
                        {t(`support.${CATEGORY_KEYS[ticket.category]}`)}
                        {isSystem && <> · {ticket.user_name} ({ticket.user_email})</>}
                        {' · '}{fmtDate(ticket.created_at, lang)}
                      </div>
                    </div>
                    <StatusPill status={ticket.status} t={t} />
                  </div>

                  {isSystem && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                      {STATUSES.map(s => (
                        <button
                          key={s} type="button"
                          className="wallet-quick-btn"
                          style={s === ticket.status ? { borderColor: STATUS_COLORS[s], color: STATUS_COLORS[s] } : undefined}
                          onClick={() => handleStatusChange(s)}
                        >
                          {t(`support.${STATUS_KEYS[s]}`)}
                        </button>
                      ))}
                    </div>
                  )}

                  <div style={{
                    display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12,
                    maxHeight: 260, overflowY: 'auto', padding: '4px 2px',
                  }}>
                    {messages.map(m => (
                      <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isMine(m) ? 'flex-end' : 'flex-start' }}>
                        {m.is_admin && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', marginBottom: 2 }}>{t('support.supportTeamLabel')}</span>
                        )}
                        <div style={{
                          maxWidth: '80%', padding: '8px 12px', borderRadius: 10, fontSize: 13, lineHeight: 1.5,
                          background: isMine(m) ? 'var(--accent)' : 'var(--surface2)',
                          color: isMine(m) ? '#fff' : 'var(--text)',
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                          {m.content}
                        </div>
                        <span style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{fmtDate(m.created_at, lang)}</span>
                      </div>
                    ))}
                    <div ref={bottomRef} />
                  </div>

                  {isClosed ? (
                    <p className="settings-note" style={{ marginTop: 10 }}>{t('support.closedNotice')}</p>
                  ) : (
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'flex-end' }}>
                      <textarea
                        className="settings-input" rows={2} value={reply}
                        placeholder={t('support.replyPlaceholder')}
                        onChange={e => setReply(e.target.value)}
                        onKeyDown={handleReplyKeyDown}
                        maxLength={2000}
                        style={{ resize: 'none', fontFamily: 'inherit', flex: 1 }}
                      />
                      <button type="button" className="btn btn-start" onClick={sendReply} disabled={sending || !reply.trim()}>
                        {t('support.sendButton')}
                      </button>
                    </div>
                  )}

                  {!isSystem && !isClosed && (
                    <button type="button" className="btn" style={{ marginTop: 10 }} onClick={handleCloseTicket}>
                      {t('support.closeTicketButton')}
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
